import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import type {
  LabelSheetData,
  PrintLabelsInput,
} from '@/features/dispensing-labels/types';
import type { AllergyFromRecord } from '@/features/patients/types';
import { api } from '@/lib/api';

const LIVE_MS = 15_000;

export interface PersonRef {
  id: string;
  fullName: string;
}

export interface NamedRef {
  id: string;
  name: string;
  code?: string;
}

export interface DispensaryDashboard {
  toDispense: number;
  dispensedToday: number;
  outOfStock: number;
  deliveries: number;
}

export interface DispensingItem {
  id: string;
  medicine: string;
  dose: string | null;
  frequency: string | null;
  durationDays: number | null;
  quantity: number;
  unit: string;
  inStock: number;
  catalogue: {
    /** The medicine code a barcode starts with (`<code>|<batch>`). */
    code?: string;
    controlled: boolean;
    psychotropic: boolean;
    highAlert: boolean;
    fridge: boolean;
    /** Counselling points (the API sends a list). */
    counselling: string[] | string | null;
  } | null;
  scanned: {
    batchNo: string | null;
    expiry: string | null;
    at: string;
    /** RL-1629: confirmed by hand (the scanner was unavailable). */
    manual?: boolean;
  } | null;
}

/** POST /dispensary/prescriptions/{id}/items/{itemId}/scan body (DispenseScanDto). */
export interface ScanBody {
  /** `<medicine code>|<batch>` from the barcode, or the batch number. */
  code: string;
  /** RL-1629: a manual scan confirmation: why the scanner was not used. */
  manualReason?: string;
  /**
   * RL-1629 review: a controlled, psychotropic or high-alert box confirmed
   * by hand: a second person's own PIN.
   */
  attestation?: { staffId: string; pin: string };
}

/** The scan answer: the prescription plus a FEFO warning. */
export type ScanResult = DispensingRx & { warning?: string | null };

/** RL-1171: `patient.allergyDetails[]` of a dispensary prescription. */
export interface DispensaryAllergyDetail {
  substance: string;
  status: 'confirmed' | 'reported';
  fromMergedRecord: boolean;
  fromRecord: AllergyFromRecord | null;
}

export interface DispensingRx {
  prescriptionId: string;
  number: string;
  signedAt: string | null;
  prescriber: PersonRef;
  patient: {
    id: string;
    fullName: string;
    identity: string | null;
    /** Labels; a patient-reported one is marked not confirmed (RL-951). */
    allergies: string[];
    /**
     * RL-1171: the same allergies, same order, with where each is recorded.
     * Optional: fall back to `allergies` when an older API omits it.
     */
    allergyDetails?: DispensaryAllergyDetail[];
  };
  branch: NamedRef;
  status: string;
  counsellingDone: boolean;
  /** RL-1633: "Clinical verification completed" as stored (older API: absent). */
  clinicalVerified?: boolean;
  clinicalVerification?: {
    at: string;
    by: { id: string; fullName: string | null } | null;
  } | null;
  labelsPrinted: number;
  /** RL-1630: true only while the second check is still missing. */
  needsSecondCheck: boolean;
  /** RL-1630: true only while the ID check is still missing. */
  needsIdCheck: boolean;
  /**
   * RL-1630: what the medicines require (a high-alert / controlled or
   * psychotropic item). Optional for an older API: fall back to `needs*`.
   */
  requiresSecondCheck?: boolean;
  requiresIdCheck?: boolean;
  secondCheckerId?: string | null;
  secondChecker?: DispensingSecondChecker | null;
  idChecked?: boolean;
  idCheck?: DispensingIdCheck | null;
  deliverable: boolean;
  delivery: {
    address: string;
    slot: string | null;
    courier: string | null;
  } | null;
  outOfStockNote: string | null;
  /** When it was handed over or packed (null before). */
  dispensedAt?: string | null;
  /**
   * Batch 17 (RL-984): the outside prescription (ORX-…) the doctor issued for
   * it from the app, a live one first; null when none. While not revoked,
   * "Dispensed" answers 409 OUTSIDE_PRESCRIPTION_ISSUED.
   */
  outsidePrescription?: DispensaryOutsidePrescription | null;
  /** RL-1636: `refill` = made when a doctor approved a refill request. */
  source?: 'consultation' | 'refill';
  refill?: DispensingRefill | null;
  items: DispensingItem[];
}

/** RL-1636: the approved refill request behind a refill prescription. */
export interface DispensingRefill {
  requestId: string;
  approvedAt: string | null;
  originalPrescription: { id: string; number: string | null };
}

/** RL-1630: the recorded second checker of a high-alert medicine. */
export interface DispensingSecondChecker {
  id: string;
  fullName: string | null;
  recordedAt: string | null;
}

/** RL-1630: the recorded ID check at hand-over. */
export interface DispensingIdCheck {
  collector: 'patient' | 'other';
  collectorName: string | null;
  idLast4: string;
  checkedAt: string;
  checkedBy: { id: string; fullName: string | null } | null;
}

/** RL-1630: GET /dispensary/second-checkers. */
export interface SecondChecker {
  id: string;
  fullName: string;
  kind: 'dispenser' | 'doctor';
  signedIn: boolean;
}

/** POST /dispensary/prescriptions/{id}/checks body (ChecksDto). */
export interface ChecksBody {
  counsellingDone?: boolean;
  /** RL-1633: the "Clinical verification completed" tick. */
  clinicalVerified?: boolean;
  /** RL-1943: the second checker's own staff PIN, typed by them. */
  secondCheckerPin?: string;
  secondCheckerId?: string;
  idChecked?: boolean;
  idCheck?: {
    collector: 'patient' | 'other';
    collectorName?: string;
    idLast4: string;
  };
}

/** RL-1159: `outsidePrescription` of a dispensary prescription. */
export interface DispensaryOutsidePrescription {
  documentId: string;
  documentNumber: string;
  issuedAt: string;
  revoked: boolean;
}

/** POST /dispensary/prescriptions/{id}/dispensed body (CompleteDto). */
export interface DispenseBody {
  /** Packed for delivery; without it the medicines are handed over. */
  delivery?: { address: string; slot: string; courier: string };
  /** RL-1159: revoke the live outside prescription in the same step. */
  revokeOutsidePrescription?: boolean;
}

/** GET /stock: one row per active medicine at the branch. */
export interface StockRow {
  id: string;
  code: string;
  name: string;
  unit: string;
  usable: number;
  held: number;
  reorderPoint: number;
  parLevel: number;
  supplier: NamedRef | null;
  perDay: number;
  daysLeft: number | null;
  nextExpiry: string | null;
  status: 'out' | 'low' | 'ok';
  flags: {
    controlled: boolean;
    psychotropic: boolean;
    highAlert: boolean;
    fridge: boolean;
  };
}

/** GET /finance/dashboard */
export interface FinanceDashboard {
  tiles: {
    claimsDue24h: number;
    queried: number;
    outstandingSen: number;
    unappliedRemittanceSen: number;
    unappliedCount: number;
  };
  pipeline: { status: string; count: number; sen: number }[];
  byDeadline: PanelClaim[];
  outstandingByPanel: { payer: string; sen: number }[];
}

export interface PanelContract {
  id: string;
  code: string;
  name: string;
  kind: string;
  status: string;
  isActive: boolean;
  windowDays: number | null;
  termsDays: number | null;
  invoiceDay: number | null;
  /** review = a draft for Finance; auto = issued automatically. */
  invoiceMode: string;
  reminderDaysBefore: number;
  submissionMethod: string | null;
  clinicCodes: Record<string, string> | null;
  exclusions: string | null;
  contactEmail: string | null;
  /** Company accounts: allowance per employee a year (sen). */
  allowanceSen: number | null;
}

export interface PanelClaim {
  id: string;
  /** The claim number (CLM-…); null on claims made before numbering. */
  reference?: string | null;
  status: string;
  amountSen: number;
  paidSen: number;
  writtenOffSen: number;
  memberNo: string | null;
  glNumber: string | null;
  payer: NamedRef & { windowDays?: number };
  branch: NamedRef;
  batch: { id: string; number: string; status: string } | null;
  bill: { id: string; number: string };
  patient: PersonRef;
  visitAt: string;
  diagnosis: { code: string; description: string } | null;
  deadlineAt: string | null;
  hoursLeft: number | null;
  blockedReason: string | null;
  queryText: string | null;
  replyText: string | null;
  /** RL-1766: when Finance marked it rejected by the panel. */
  rejectedAt?: string | null;
  /** RL-1766: a whole-claim write-off waiting for the clinic manager. */
  writeOffRequest?: {
    requestedAt: string;
    requestedById: string | null;
    reason: string | null;
  } | null;
  /** RL-1761: the patient's latest "I don't recognise this" flag (app). */
  patientFlag?: {
    id: string;
    reason: string;
    note: string | null;
    status: 'open' | 'resolved';
    createdAt: string;
    resolvedAt: string | null;
  } | null;
}

export interface ClaimBatch {
  id: string;
  number: string;
  payer: NamedRef;
  branch: { name: string };
  status: string;
  method: string;
  source: string;
  totalSen: number;
  paidSen: number;
  /** RL-1771: what the panel still owes, from the claims. */
  outstandingSen?: number;
  claims: number;
  issuedAt: string | null;
  createdAt: string;
  dueAt: string | null;
}

/** GET /finance/schedules: each panel's monthly invoice run. */
export interface InvoiceSchedule {
  id: string;
  name: string;
  invoiceDay: number | null;
  invoiceMode: string;
  reminderDaysBefore: number;
  nextRun: string | null;
  nextReminder: string | null;
  runs: {
    /** RL-1776: the run's id (API #212). */
    id?: string;
    at: string;
    result: string;
    claims: number;
    batchId: string | null;
    note: string | null;
    simulatedClock: boolean;
  }[];
}

/** GET /finance/remittances */
export interface Remittance {
  id: string;
  payer: NamedRef;
  reference: string;
  amountSen: number;
  receivedOn: string;
  status: string;
  allocatedSen: number;
  allocations: {
    id: string;
    claimId: string;
    paidSen: number;
    deductionSen: number;
    deductionReason: string | null;
    decision: string | null;
  }[];
}

/** GET /finance/member-verifications */
export interface MemberVerification {
  id: string;
  patientId: string;
  cardType: string;
  cardNoLast4: string;
  /** App requests, while pending: the full number (erased at the decision). */
  membershipNo?: string | null;
  email?: string | null;
  /** App requests, while pending: open it with GET …/card-photo. */
  cardPhoto?: { contentType: string; sizeBytes: number; path: string } | null;
  source: string;
  status: string;
  submittedAt: string;
  decidedAt: string | null;
  reason: string | null;
  validUntil: string | null;
  patient: { id: string; fullName: string; memberPriceStatus: string | null };
}

export interface WellbeingSession {
  id: string;
  patient: PersonRef;
  practitioner: PersonRef;
  startsAt: string;
  type: string;
  status: string;
  urgent: boolean;
  serviceCode: string;
  /** When it was cancelled (null while not cancelled). */
  cancelledAt?: string | null;
  /**
   * RL-2020: who cancelled it: the patient in the app, or the clinic
   * (Cancel session); null while not cancelled.
   */
  cancelledBy?: 'patient' | 'clinic' | null;
  screening: { tool: string; score: number | null } | null;
  planSharedAt: string | null;
  closedAt: string | null;
  canReadNotes: boolean;
}

export interface WellbeingDashboard {
  day: string;
  today: WellbeingSession[];
  /** alerts (RL-1006): open hard-time results from patients' own screening in the app. */
  counts: {
    today: number;
    urgent: number;
    toClose: number;
    alerts?: number;
    /** API RL-1861: my sessions marked no-show this calendar month. */
    noShowsThisMonth?: number;
  };
}

/** RL-1006: a patient whose own screening in the app came out hard_time. */
export interface WellbeingAlert {
  id: string;
  patient: { id: string; fullName: string; phone: string | null };
  branch: { id: string; name: string } | null;
  result: 'hard_time';
  total: number;
  submittedAt: string;
  bookedSession: { id: string; startsAt: string; urgent: boolean } | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
}

export interface StaffRow {
  id: string;
  email: string;
  fullName: string;
  greetingName: string | null;
  status: string;
  mfaEnabled?: boolean;
  mmcNumber: string | null;
  apcValidUntil: string | null;
  lastSignedInAt?: string | null;
  /** RL-1785: the last moment they can sign in (null = no end). */
  accessEndsAt?: string | null;
  /** RL-1793: from the accepted invitation; null when made without one. */
  invitedById?: string | null;
  invitedByName?: string | null;
  invitedAt?: string | null;
  roles?: {
    role: string;
    branch: string | null;
    /** RL-1782: for Remove role. */
    assignmentId?: string;
    roleName?: string;
    branchName?: string;
    /** When the assignment ends (null = no end). */
    endsAt?: string | null;
  }[];
}

export interface ResetStaffMfaResult {
  targetStaffId: string;
  mfaWasEnabled: boolean;
  recoveryCodesRevoked: number;
  sessionsRevoked: number;
  forceMfaSetup: true;
}

export interface PermissionProposal {
  id: string;
  roleKey: string;
  permission: string;
  /** grant = add the permission to the role, revoke = remove it. */
  change: 'grant' | 'revoke';
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  proposedAt: string;
  proposedById: string;
  proposedBy: string | null;
  decidedAt: string | null;
  decidedById: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  /**
   * RL-1792/RL-2019: decided by the proposer, because nobody else could
   * approve permission changes then (the lone Super admin), with a note.
   */
  selfApproved?: boolean;
  /**
   * RL-1792/RL-2019 (GET /admin/permission-changes only): your own pending
   * proposal that you may decide yourself, as nobody else can approve now.
   */
  selfApprovable?: boolean;
}

/** GET /admin/roles: effective grants, the catalogue baseline and approved overrides (RL-1035). */
export interface PermissionMatrix {
  permissions: {
    key: string;
    description: string | null;
    inCatalogue: boolean;
    protected: boolean;
  }[];
  roles: {
    key: string;
    name: string;
    protected: boolean;
    permissions: string[];
    baseline: string[];
    overrides: {
      id: string;
      permission: string;
      change: 'grant' | 'revoke';
      decidedAt: string | null;
    }[];
  }[];
}

export interface IntegrationStatus {
  id?: string;
  name: string;
  provider?: string;
  status: string;
  lastEvent?: string | null;
  keyRotatedAt?: string | null;
  /** RL-1824: fingerprint of the key in use (never the key itself). */
  keyFingerprint?: string | null;
  simulated?: boolean;
}

function useBranchKey() {
  return useBranch().selected?.id ?? 'all';
}

export function useDispensaryDashboard() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['dispensary', 'dashboard', branch],
    queryFn: ({ signal }) =>
      api
        .get<DispensaryDashboard>('/dispensary/dashboard', { signal })
        .then((r) => r.data),
    enabled: branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useDispensingQueue(status = 'to_dispense') {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['dispensary', 'queue', branch, status],
    queryFn: ({ signal }) =>
      api
        .get<DispensingRx[]>('/dispensary/prescriptions', {
          signal,
          query: { status },
        })
        .then((r) => r.data),
    enabled: branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

/** RL-1630: eligible second checkers at the selected branch (caller left out). */
export function useSecondCheckers(rx: DispensingRx, enabled: boolean) {
  // Review: always the prescription's branch, so it works with "All branches".
  const branchId = rx.branch.id;
  return useQuery({
    queryKey: ['dispensary', 'second-checkers', branchId, rx.prescriptionId],
    queryFn: ({ signal }) =>
      api
        .get<SecondChecker[]>('/dispensary/second-checkers', {
          signal,
          // RL-1629 review: nobody who scanned one of its boxes.
          query: { branchId, prescriptionId: rx.prescriptionId },
        })
        .then((r) => r.data),
    enabled,
    staleTime: 30_000,
  });
}

export function useDispensaryActions() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['dispensary'] });
    void qc.invalidateQueries({ queryKey: ['stock'] });
  };
  const updatePrescription = (updated: DispensingRx) => {
    qc.setQueriesData<DispensingRx[]>(
      { queryKey: ['dispensary', 'queue'] },
      (current) =>
        current?.map((rx) =>
          rx.prescriptionId === updated.prescriptionId ? updated : rx,
        ),
    );
    refresh();
  };
  return {
    checks: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: ChecksBody }) =>
        (
          await api.post<DispensingRx>(
            `/dispensary/prescriptions/${id}/checks`,
            body,
          )
        ).data,
      onSuccess: updatePrescription,
    }),
    // RL-1629: one box scanned (or confirmed by hand) against one item.
    scan: useMutation({
      retry: false,
      mutationFn: async ({
        id,
        itemId,
        body,
      }: {
        id: string;
        itemId: string;
        body: ScanBody;
      }) =>
        (
          await api.post<ScanResult>(
            `/dispensary/prescriptions/${id}/items/${itemId}/scan`,
            body,
          )
        ).data,
      onSuccess: updatePrescription,
    }),
    // Records a print and returns the label data. A reprint needs `reason`
    // (422 REPRINT_REASON otherwise). Never retried automatically.
    labels: useMutation({
      retry: false,
      mutationFn: async ({ id, reason }: PrintLabelsInput) =>
        (
          await api.post<LabelSheetData>(
            `/dispensary/prescriptions/${id}/labels`,
            reason ? { reason } : {},
          )
        ).data,
      onSuccess: refresh,
    }),
    // RL-1301 (staging only): the audited TEST print-record for dedicated QA
    // prescriptions. Same rules as `labels`; prints nothing. Never retried.
    testRecordLabels: useMutation({
      retry: false,
      mutationFn: async ({ id, reason }: PrintLabelsInput) =>
        (
          await api.post<LabelSheetData>(
            `/dispensary/prescriptions/${id}/labels/test-record`,
            reason ? { reason } : {},
          )
        ).data,
      onSuccess: refresh,
    }),
    dispense: useMutation({
      mutationFn: (v: { id: string; body?: DispenseBody }) =>
        api.post<DispensingRx>(
          `/dispensary/prescriptions/${v.id}/dispensed`,
          v.body ?? {},
        ),
      onSuccess: refresh,
    }),
    outOfStock: useMutation({
      mutationFn: (v: { id: string; note: string }) =>
        api.post<DispensingRx>(
          `/dispensary/prescriptions/${v.id}/out-of-stock`,
          { note: v.note },
        ),
      onSuccess: refresh,
    }),
  };
}

export function useStockOverview() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['stock', 'overview', branch],
    queryFn: ({ signal }) =>
      api.get<StockRow[]>('/stock', { signal }).then((r) => r.data),
    enabled: branch !== 'all',
  });
}

export function useStockCounts() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['stock', 'counts', branch],
    queryFn: ({ signal }) =>
      api.get<unknown[]>('/stock/counts', { signal }).then((r) => r.data),
    enabled: branch !== 'all',
  });
}

export function useStockActions() {
  const qc = useQueryClient();
  return {
    draftOrder: useMutation({
      mutationFn: () => api.post('/stock/orders/draft-from-reorder'),
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
    }),
    startCount: useMutation({
      mutationFn: (kind: string) => api.post('/stock/counts', { kind }),
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
    }),
  };
}

export function useFinanceDashboard() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['finance', 'dashboard', branch],
    queryFn: ({ signal }) =>
      api
        .get<FinanceDashboard>('/finance/dashboard', { signal })
        .then((r) => r.data),
    refetchInterval: LIVE_MS,
  });
}

export function usePanels() {
  return useQuery({
    queryKey: ['finance', 'panels'],
    queryFn: ({ signal }) =>
      api
        .get<PanelContract[]>('/finance/panels', { signal })
        .then((r) => r.data),
  });
}

export function useClaims(status = 'all') {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['finance', 'claims', branch, status],
    queryFn: ({ signal }) =>
      api
        .get<PanelClaim[]>('/finance/claims', { signal, query: { status } })
        .then((r) => r.data),
  });
}

export function useFinanceActions() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ['finance'] });
  return {
    validateClaims: useMutation({
      mutationFn: () => api.post('/finance/claims/validate'),
      onSuccess: refresh,
    }),
    createBatch: useMutation({
      mutationFn: () => api.post<ClaimBatch[]>('/finance/invoices', {}),
      onSuccess: refresh,
    }),
    issueBatch: useMutation({
      mutationFn: (v: { id: string; method: 'portal' | 'email' | 'export' }) =>
        api.post<ClaimBatch>(`/finance/invoices/${v.id}/issue`, {
          method: v.method,
        }),
      onSuccess: refresh,
    }),
    replyClaim: useMutation({
      mutationFn: (v: { id: string; text: string }) =>
        api.post<PanelClaim>(`/finance/claims/${v.id}/reply`, { text: v.text }),
      onSuccess: refresh,
    }),
    billPatient: useMutation({
      mutationFn: (v: { id: string; text: string }) =>
        api.post<PanelClaim>(`/finance/claims/${v.id}/bill-patient`, {
          text: v.text,
        }),
      onSuccess: refresh,
    }),
    decideMember: useMutation({
      mutationFn: (v: { id: string; approve: boolean; reason?: string }) =>
        api.post<MemberVerification>(
          `/finance/member-verifications/${v.id}/decision`,
          { approve: v.approve, reason: v.reason },
        ),
      onSuccess: refresh,
    }),
    autoMatch: useMutation({
      mutationFn: (id: string) =>
        api.post<Remittance>(`/finance/remittances/${id}/auto-match`),
      onSuccess: refresh,
    }),
    knockOff: useMutation({
      mutationFn: (id: string) =>
        api.post<Remittance>(`/finance/remittances/${id}/knock-off`),
      onSuccess: refresh,
    }),
    /** RL-1980: "Undo match" before the knock-off (the matches are kept as history). */
    unmatch: useMutation({
      mutationFn: (v: { id: string; reason?: string }) =>
        api.post<Remittance>(`/finance/remittances/${v.id}/unmatch`, {
          reason: v.reason,
        }),
      onSuccess: refresh,
    }),
  };
}

export function useInvoices() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['finance', 'invoices', branch],
    queryFn: ({ signal }) =>
      api
        .get<ClaimBatch[]>('/finance/invoices', { signal })
        .then((r) => r.data),
  });
}

/** GET /finance/ageing: one row per issued invoice, bucketed by age. */
export interface AgeingResponse {
  buckets: { bucket: string; sen: number }[];
  rows: {
    id: string;
    number: string;
    payer: NamedRef & { termsDays: number | null };
    outstandingSen: number;
    issuedAt: string | null;
    ageDays: number;
    bucket: string;
    overdue: boolean;
    nextAction: string | null;
  }[];
  totalSen: number;
}

export function useRemittances() {
  return useQuery({
    queryKey: ['finance', 'remittances'],
    queryFn: ({ signal }) =>
      api
        .get<Remittance[]>('/finance/remittances', { signal })
        .then((r) => r.data),
  });
}

/** `status` 'all' lists every request (no filter). */
export function useMemberVerifications(status = 'pending') {
  return useQuery({
    queryKey: ['finance', 'member-verifications', status],
    queryFn: ({ signal }) =>
      api
        .get<MemberVerification[]>('/finance/member-verifications', {
          signal,
          query: status === 'all' ? {} : { status },
        })
        .then((r) => r.data),
  });
}

export function useWellbeingDashboard() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['wellbeing', 'dashboard', branch],
    queryFn: ({ signal }) =>
      api
        .get<WellbeingDashboard>('/wellbeing/dashboard', { signal })
        .then((r) => r.data),
    enabled: branch !== 'all',
  });
}

export function useWellbeingSessions(mine = false) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['wellbeing', 'sessions', branch, mine],
    queryFn: ({ signal }) =>
      api
        .get<WellbeingSession[]>('/wellbeing/sessions', {
          signal,
          query: { mine },
        })
        .then((r) => r.data),
    enabled: branch !== 'all',
  });
}

/** Open hard-time alerts (practitioners: wellbeing_sessions:manage). Not branch-scoped. */
export function useWellbeingAlerts(enabled: boolean) {
  return useQuery({
    queryKey: ['wellbeing', 'alerts'],
    queryFn: ({ signal }) =>
      api
        .get<WellbeingAlert[]>('/wellbeing/alerts', { signal })
        .then((r) => r.data),
    enabled,
  });
}

export function useWellbeingAccess() {
  return useQuery({
    queryKey: ['wellbeing', 'access'],
    queryFn: ({ signal }) =>
      api
        .get<{ practitioners: PersonRef[]; grants: unknown[] }>(
          '/wellbeing/access',
          {
            signal,
          },
        )
        .then((r) => r.data),
  });
}

export function useWellbeingActions() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ['wellbeing'] });
  return {
    crisis: useMutation({
      mutationFn: (id: string) => api.post(`/wellbeing/sessions/${id}/crisis`),
      onSuccess: refresh,
    }),
    sharePlan: useMutation({
      mutationFn: (id: string) =>
        api.post(`/wellbeing/sessions/${id}/share-plan`),
      onSuccess: refresh,
    }),
    close: useMutation({
      mutationFn: (id: string) => api.post(`/wellbeing/sessions/${id}/close`),
      onSuccess: refresh,
    }),
    acknowledgeAlert: useMutation({
      mutationFn: (id: string) =>
        api.post(`/wellbeing/alerts/${id}/acknowledge`),
      onSuccess: refresh,
    }),
  };
}

export function useStaff() {
  return useQuery({
    queryKey: ['admin', 'staff'],
    queryFn: ({ signal }) =>
      api.get<StaffRow[]>('/admin/staff', { signal }).then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useResetStaffMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ staffId, reason }: { staffId: string; reason: string }) =>
      api
        .post<ResetStaffMfaResult>(`/admin/staff/${staffId}/mfa/reset`, {
          reason,
        })
        .then((response) => response.data),
    onSuccess: (_result, { staffId }) => {
      queryClient.setQueryData<StaffRow[]>(['admin', 'staff'], (rows) =>
        rows?.map((row) =>
          row.id === staffId ? { ...row, mfaEnabled: false } : row,
        ),
      );
    },
  });
}

export function usePermissionMatrix() {
  return useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: ({ signal }) =>
      api.get<PermissionMatrix>('/admin/roles', { signal }).then((r) => r.data),
  });
}

export function usePermissionChanges() {
  return useQuery({
    queryKey: ['admin', 'permission-changes'],
    queryFn: ({ signal }) =>
      api
        .get<PermissionProposal[]>('/admin/permission-changes', { signal })
        .then((r) => r.data),
  });
}

/** Propose / approve / reject. The server enforces who may do what (RL-1035). */
export function usePermissionChangeActions() {
  const qc = useQueryClient();
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'permission-changes'] }),
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
    ]);
  return {
    propose: useMutation({
      mutationFn: (body: {
        roleKey: string;
        permission: string;
        change: 'grant' | 'revoke';
        reason: string;
      }) =>
        api
          .post<PermissionProposal>('/admin/permission-changes', body)
          .then((r) => r.data),
      onSettled: refresh,
    }),
    approve: useMutation({
      mutationFn: ({ id, note }: { id: string; note?: string }) =>
        api
          .post<PermissionProposal>(
            `/admin/permission-changes/${id}/approve`,
            note ? { note } : {},
          )
          .then((r) => r.data),
      onSettled: refresh,
    }),
    reject: useMutation({
      mutationFn: ({ id, note }: { id: string; note: string }) =>
        api
          .post<PermissionProposal>(`/admin/permission-changes/${id}/reject`, {
            note,
          })
          .then((r) => r.data),
      onSettled: refresh,
    }),
  };
}

export function useIntegrations() {
  return useQuery({
    queryKey: ['admin', 'integrations'],
    queryFn: ({ signal }) =>
      api
        .get<IntegrationStatus[]>('/admin/integrations', { signal })
        .then((r) => r.data),
  });
}
